import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Backdrop,
} from '@mui/material';
import FormularioFiltros from '../components/FormularioFiltros';
import AlertaPessoasSemPix from '../components/AlertaPessoasSemPix';
import ResumoLancamentos from '../components/ResumoLancamentos';
import TabelaLancamentos from '../components/TabelaLancamentos';
import Layout from '../components/Layout';
import {
  buscarLancamentos,
  downloadArquivoCNAB240,
  RespostaLancamentos,
  Filtros,
} from '../services/api';
import { colors } from '../theme/expertiseTheme';

/**
 * Página principal de gerenciamento de pagamentos PIX
 */
const PagamentosPage: React.FC = () => {
  // Estado dos filtros
  const [filtros, setFiltros] = useState<Filtros>({
    conta_id: '81043',
    data_inicio: getDataInicio(),
    data_fim: getDataFim(),
  });

  // Estado dos dados
  const [dados, setDados] = useState<RespostaLancamentos | null>(null);
  const [loading, setLoading] = useState(false);

  // Estado das notificações
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'info' | 'success' | 'warning' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  /**
   * Função auxiliar para obter data de início do mês atual
   */
  function getDataInicio() {
    const hoje = new Date();
    return new Date(hoje.getFullYear(), hoje.getMonth(), 1)
      .toISOString()
      .split('T')[0];
  }

  /**
   * Função auxiliar para obter data de fim do mês atual
   */
  function getDataFim() {
    const hoje = new Date();
    return new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0)
      .toISOString()
      .split('T')[0];
  }

  /**
   * Busca os lançamentos na API
   */
  const handleBuscarLancamentos = async () => {
    // Validação básica
    if (!filtros.conta_id || !filtros.data_inicio || !filtros.data_fim) {
      mostrarNotificacao('Por favor, preencha todos os campos', 'warning');
      return;
    }

    if (new Date(filtros.data_inicio) > new Date(filtros.data_fim)) {
      mostrarNotificacao('Data de início não pode ser maior que data de fim', 'error');
      return;
    }

    setLoading(true);
    try {
      const resultado = await buscarLancamentos(filtros);
      setDados(resultado);

      if (resultado.total === 0) {
        mostrarNotificacao('Nenhum lançamento encontrado para os filtros informados', 'info');
      } else {
        mostrarNotificacao(
          `✅ ${resultado.total} lançamento(s) encontrado(s) para ${resultado.totalPessoas} pessoa(s)`,
          'success'
        );
      }
    } catch (error: any) {
      console.error('Erro ao buscar lançamentos:', error);
      mostrarNotificacao(`Erro ao buscar lançamentos: ${error.message}`, 'error');
      setDados(null);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Faz o download do arquivo CNAB240
   */
  const handleDownloadArquivo = async () => {
    if (!dados || dados.pessoasComPix === 0) {
      mostrarNotificacao('Não há pessoas com PIX para gerar o arquivo', 'warning');
      return;
    }

    setLoading(true);
    try {
      await downloadArquivoCNAB240(filtros);
      mostrarNotificacao(
        `✅ Arquivo CNAB240 baixado com sucesso! (${dados.pessoasComPix} pagamentos)`,
        'success'
      );
    } catch (error: any) {
      console.error('Erro ao baixar arquivo:', error);
      mostrarNotificacao(`Erro ao gerar arquivo: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Exibe uma notificação
   */
  const mostrarNotificacao = (
    message: string,
    severity: 'info' | 'success' | 'warning' | 'error' = 'info'
  ) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  /**
   * Fecha a notificação
   */
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Busca automática ao montar o componente (opcional)
  useEffect(() => {
    // handleBuscarLancamentos();
  }, []);

  return (
    <Layout
      title="Pagamentos PIX"
      subtitle="Sistema de Gerenciamento de Pagamentos via PIX - ERP Integration"
    >
      {/* Formulário de Filtros */}
      <FormularioFiltros
        filtros={filtros}
        onChange={setFiltros}
        onBuscar={handleBuscarLancamentos}
        loading={loading}
      />

      {/* Alerta de Pessoas Sem PIX */}
      {dados && dados.lancamentosSemPix && dados.lancamentosSemPix.length > 0 && (
        <AlertaPessoasSemPix pessoasSemPix={dados.lancamentosSemPix} />
      )}

      {/* Resumo dos Lançamentos */}
      {dados && (
        <ResumoLancamentos
          dados={dados}
          onDownload={handleDownloadArquivo}
          loading={loading}
        />
      )}

      {/* Tabela de Lançamentos */}
      {dados && dados.lancamentos && dados.lancamentos.length > 0 && (
        <TabelaLancamentos lancamentos={dados.lancamentos} />
      )}

      {/* Backdrop de Loading */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ color: colors.accent }} size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Processando...
          </Typography>
        </Box>
      </Backdrop>

      {/* Snackbar de Notificações */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default PagamentosPage;
